import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { PortfolioHolding } from '../api';
import { API_BASE } from '../config';

async function portfolioFetch(endpoint: string, token: string | null, options: RequestInit = {}) {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

interface RealtimeQuote {
  currentPrice: number;
  change: number;
  percentChange: number;
}

interface HoldingWithPrice extends PortfolioHolding {
  currentPrice: number | null;
  change: number | null;
}

export default function Portfolio() {
  const { token } = useAuth();
  const [holdings, setHoldings] = useState<PortfolioHolding[]>([]);
  const [systemSymbols, setSystemSymbols] = useState<string[]>([]);
  const [prices, setPrices] = useState<Record<string, RealtimeQuote>>({});
  const [usdToGbp, setUsdToGbp] = useState<number>(0.79);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add form
  const [symbol, setSymbol] = useState('');
  const [quantity, setQuantity] = useState('');
  const [buyPrice, setBuyPrice] = useState('');
  const [adding, setAdding] = useState(false);
  const [goalInputs, setGoalInputs] = useState<Record<number, string>>({});

  // Sell form state: which row is in sell-mode
  const [sellIndex, setSellIndex] = useState<number | null>(null);
  const [sellQty, setSellQty] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [selling, setSelling] = useState(false);

  const loadSystemSymbols = useCallback(async () => {
    try {
      const data = await portfolioFetch('/symbols', token);
      const symbols = Array.isArray(data) ? data : [];
      setSystemSymbols(symbols);
      if (!symbol && symbols.length > 0) setSymbol(symbols[0]);
    } catch {
      setSystemSymbols([]);
    }
  }, [symbol, token]);

  const loadFxRate = useCallback(async () => {
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await res.json();
      const rate = Number(data?.rates?.GBP);
      if (Number.isFinite(rate) && rate > 0) setUsdToGbp(rate);
    } catch {
      // Keep fallback value
    }
  }, []);

  const loadPortfolio = useCallback(async () => {
    try {
      const data = await portfolioFetch('/portfolio', token);
      setHoldings(data.portfolio || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch real-time prices from Finnhub via portfolio/quotes endpoint
  const loadPrices = useCallback(async (items: PortfolioHolding[]) => {
    const symbols = Array.from(new Set(items.map(h => h.symbol)));
    try {
      const data = await portfolioFetch('/portfolio/quotes', token, {
        method: 'POST',
        body: JSON.stringify({ symbols }),
      });
      setPrices(data.quotes || {});
    } catch { /* fall back to empty */ }
  }, [token]);

  useEffect(() => { loadPortfolio(); }, [loadPortfolio]);
  useEffect(() => { loadSystemSymbols(); }, [loadSystemSymbols]);
  useEffect(() => { loadFxRate(); }, [loadFxRate]);
  useEffect(() => { if (holdings.length > 0) loadPrices(holdings); }, [holdings, loadPrices]);
  useEffect(() => {
    const next: Record<number, string> = {};
    holdings.forEach((h, i) => {
      next[i] = h.goalPrice != null ? String(h.goalPrice) : '';
    });
    setGoalInputs(next);
  }, [holdings]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!systemSymbols.includes(symbol)) {
      setError('Please select a valid symbol from the system list');
      return;
    }
    setAdding(true);
    try {
      const data = await portfolioFetch('/portfolio/add', token, {
        method: 'POST',
        body: JSON.stringify({ symbol, quantity: parseFloat(quantity), buyPrice: parseFloat(buyPrice) }),
      });
      setHoldings(data.portfolio || []);
      setSymbol(''); setQuantity(''); setBuyPrice('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (index: number) => {
    try {
      const data = await portfolioFetch('/portfolio/remove', token, {
        method: 'POST',
        body: JSON.stringify({ index }),
      });
      setHoldings(data.portfolio || []);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSell = async (index: number) => {
    setError('');
    setSelling(true);
    try {
      const data = await portfolioFetch('/portfolio/sell', token, {
        method: 'POST',
        body: JSON.stringify({ index, soldQuantity: parseFloat(sellQty), soldPrice: parseFloat(sellPrice) }),
      });
      setHoldings(data.portfolio || []);
      setSellIndex(null);
      setSellQty('');
      setSellPrice('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSelling(false);
    }
  };

  const handleSaveGoal = async (index: number) => {
    setError('');
    const raw = (goalInputs[index] || '').trim();
    const parsed = raw === '' ? null : parseFloat(raw);
    if (parsed != null && (!Number.isFinite(parsed) || parsed <= 0)) {
      setError('Goal price must be a positive number');
      return;
    }

    try {
      const data = await portfolioFetch('/portfolio/goal', token, {
        method: 'POST',
        body: JSON.stringify({ index, goalPrice: parsed }),
      });
      setHoldings(data.portfolio || []);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const openSellForm = (index: number, currentPrice: number | null) => {
    setSellIndex(index);
    setSellQty('');
    setSellPrice(currentPrice != null ? currentPrice.toFixed(2) : '');
  };

  // Build enriched holdings with current prices
  const enriched: HoldingWithPrice[] = holdings.map(h => ({
    ...h,
    currentPrice: prices[h.symbol]?.currentPrice ?? null,
    change: prices[h.symbol]?.currentPrice != null
      ? prices[h.symbol].currentPrice - h.buyPrice
      : null,
  }));

  // Portfolio totals (only count remaining unsold shares)
  const totalCost = enriched.reduce((sum, h) => {
    const remaining = h.quantity - (h.soldQuantity || 0);
    return sum + h.buyPrice * remaining;
  }, 0);
  const totalValue = enriched.reduce((sum, h) => {
    if (h.currentPrice == null) return sum;
    const remaining = h.quantity - (h.soldQuantity || 0);
    return sum + h.currentPrice * remaining;
  }, 0);
  const totalRealizedPnL = enriched.reduce((sum, h) => {
    if (!h.soldQuantity || !h.soldPrice) return sum;
    return sum + (h.soldPrice - h.buyPrice) * h.soldQuantity;
  }, 0);
  const totalGoalProfit = enriched.reduce((sum, h) => {
    if (h.goalPrice == null) return sum;
    const remaining = h.quantity - (h.soldQuantity || 0);
    return sum + (h.goalPrice - h.buyPrice) * remaining;
  }, 0);
  const totalExpectedGoalValue = enriched.reduce((sum, h) => {
    if (h.goalPrice == null) return sum;
    const remaining = h.quantity - (h.soldQuantity || 0);
    return sum + h.goalPrice * remaining;
  }, 0);
  const hasAnyGoal = enriched.some((h) => h.goalPrice != null);
  const totalGoalProfitWithRealized = hasAnyGoal ? totalGoalProfit + totalRealizedPnL : null;
  const hasAllPrices = enriched.every(h => h.currentPrice != null);
  const unrealizedPnL = hasAllPrices ? totalValue - totalCost : null;
  const totalPnL = unrealizedPnL != null ? unrealizedPnL + totalRealizedPnL : null;
  const totalPnLPct = totalCost > 0 && totalPnL != null ? (totalPnL / totalCost) * 100 : null;

  const formatUsd = (amount: number) =>
    `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatGbp = (amount: number) =>
    `£${(amount * usdToGbp).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="loading">Loading portfolio…</div>;

  return (
    <div>
      <h2>My Portfolio</h2>

      {/* Add holding form */}
      <form onSubmit={handleAdd} className="portfolio-add-form">
        <span className="portfolio-add-hint">Only symbols approved by admin can be added.</span>
        <select
          value={symbol}
          onChange={e => setSymbol(e.target.value)}
          required
          disabled={systemSymbols.length === 0}
        >
          {systemSymbols.length === 0 && <option value="">No symbols</option>}
          {systemSymbols.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input
          type="number"
          placeholder="Quantity"
          value={quantity}
          onChange={e => setQuantity(e.target.value)}
          required
          min="0.001"
          step="any"
        />
        <input
          type="number"
          placeholder="Buy Price ($)"
          value={buyPrice}
          onChange={e => setBuyPrice(e.target.value)}
          required
          min="0.01"
          step="any"
        />
        <button type="submit" disabled={adding || systemSymbols.length === 0 || !symbol}>
          {adding ? 'Adding…' : 'Add Holding'}
        </button>
      </form>

      {error && <div className="error-msg">{error}</div>}

      {/* Summary cards */}
      {enriched.length > 0 && (
        <div className="portfolio-summary">
          <div className="portfolio-summary-card">
            <div className="portfolio-summary-label">Total Invested</div>
            <div className="portfolio-summary-value">{formatUsd(totalCost)}</div>
            <div className="portfolio-summary-subvalue">{formatGbp(totalCost)}</div>
          </div>
          <div className="portfolio-summary-card">
            <div className="portfolio-summary-label">Current Value</div>
            <div className="portfolio-summary-value">
              {hasAllPrices ? formatUsd(totalValue) : '—'}
            </div>
            <div className="portfolio-summary-subvalue">{hasAllPrices ? formatGbp(totalValue) : '—'}</div>
          </div>
          {totalRealizedPnL !== 0 && (
            <div className={`portfolio-summary-card ${totalRealizedPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
              <div className="portfolio-summary-label">Realized P/L</div>
              <div className="portfolio-summary-value">
                {totalRealizedPnL >= 0 ? '+' : ''}{formatUsd(Math.abs(totalRealizedPnL)).replace('$', '$')}
              </div>
              <div className="portfolio-summary-subvalue">
                {totalRealizedPnL >= 0 ? '+' : ''}{formatGbp(Math.abs(totalRealizedPnL)).replace('£', '£')}
              </div>
            </div>
          )}
          {hasAnyGoal && (
            <div className="portfolio-summary-card">
              <div className="portfolio-summary-label">Expected Value At Goal</div>
              <div className="portfolio-summary-value">{formatUsd(totalExpectedGoalValue)}</div>
              <div className="portfolio-summary-subvalue">{formatGbp(totalExpectedGoalValue)}</div>
            </div>
          )}
          {hasAnyGoal && (
            <div className={`portfolio-summary-card ${totalGoalProfitWithRealized != null ? (totalGoalProfitWithRealized >= 0 ? 'pnl-positive' : 'pnl-negative') : ''}`}>
              <div className="portfolio-summary-label">Goal Total Profit</div>
              <div className="portfolio-summary-value">
                {totalGoalProfitWithRealized != null ? (
                  <span>
                    {totalGoalProfitWithRealized >= 0 ? '+' : ''}
                    {formatUsd(Math.abs(totalGoalProfitWithRealized)).replace('$', '$')}
                  </span>
                ) : '—'}
              </div>
              <div className="portfolio-summary-subvalue">
                {totalGoalProfitWithRealized != null
                  ? `${totalGoalProfitWithRealized >= 0 ? '+' : ''}${formatGbp(Math.abs(totalGoalProfitWithRealized)).replace('£', '£')}`
                  : '—'}
              </div>
            </div>
          )}
          <div className={`portfolio-summary-card ${totalPnL != null ? (totalPnL >= 0 ? 'pnl-positive' : 'pnl-negative') : ''}`}>
            <div className="portfolio-summary-label">Total P/L</div>
            <div className="portfolio-summary-value">
              {totalPnL != null ? (
                <>
                  <span>{totalPnL >= 0 ? '+' : ''}{formatUsd(Math.abs(totalPnL)).replace('$', '$')}</span>
                  {totalPnLPct != null && <span className="portfolio-pct"> ({totalPnLPct.toFixed(2)}%)</span>}
                </>
              ) : '—'}
            </div>
            <div className="portfolio-summary-subvalue">
              {totalPnL != null ? `${totalPnL >= 0 ? '+' : ''}${formatGbp(Math.abs(totalPnL)).replace('£', '£')}` : '—'}
            </div>
          </div>
        </div>
      )}

      {/* Holdings table */}
      {enriched.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px', marginTop: '24px' }}>
          <p style={{ color: 'var(--text-muted)' }}>No holdings yet. Add your first stock above.</p>
        </div>
      ) : (
        <div className="portfolio-table-wrap">
          <table className="portfolio-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Qty</th>
                <th>Buy Price</th>
                <th>Current Price</th>
                <th>Sold</th>
                <th>Remaining</th>
                <th>Total Cost</th>
                <th>Current Total</th>
                <th>Goal Price</th>
                <th>Goal Profit</th>
                <th>Total P/L</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {enriched.map((h, i) => {
                const soldQty = h.soldQuantity || 0;
                const remaining = h.quantity - soldQty;
                const cost = h.buyPrice * remaining;
                const currentTotal = h.currentPrice != null ? h.currentPrice * remaining : null;
                const unrealized = currentTotal != null ? currentTotal - cost : null;
                const realized = soldQty > 0 && h.soldPrice ? (h.soldPrice - h.buyPrice) * soldQty : 0;
                const goalProfit = h.goalPrice != null ? ((h.goalPrice - h.buyPrice) * remaining) + realized : null;
                const rowPnL = unrealized != null ? unrealized + realized : null;
                const pnlPct = cost > 0 && rowPnL != null ? (rowPnL / (h.buyPrice * h.quantity)) * 100 : null;
                const pnlClass = rowPnL != null ? (rowPnL >= 0 ? 'profit' : 'loss') : '';
                const goalClass = goalProfit != null ? (goalProfit >= 0 ? 'profit' : 'loss') : '';

                return (
                  <React.Fragment key={i}>
                    <tr>
                      <td className="symbol-cell">{h.symbol}</td>
                      <td>{h.quantity}</td>
                      <td>${h.buyPrice.toFixed(2)}</td>
                      <td>{h.currentPrice != null ? `$${h.currentPrice.toFixed(2)}` : '—'}</td>
                      <td>
                        {soldQty > 0 ? (
                          <span className="sold-info">{soldQty} @ ${h.soldPrice?.toFixed(2)}</span>
                        ) : '—'}
                      </td>
                      <td>{remaining}</td>
                      <td>${cost.toFixed(2)}</td>
                      <td>{currentTotal != null ? `$${currentTotal.toFixed(2)}` : '—'}</td>
                      <td>
                        <div className="goal-input-wrap">
                          <input
                            className="goal-price-input"
                            type="number"
                            min="0.01"
                            step="any"
                            placeholder="Set"
                            value={goalInputs[i] ?? ''}
                            onChange={(e) => setGoalInputs((prev) => ({ ...prev, [i]: e.target.value }))}
                          />
                          <button className="goal-save-btn" onClick={() => handleSaveGoal(i)}>
                            Save
                          </button>
                        </div>
                      </td>
                      <td className={goalClass}>
                        {goalProfit != null ? `${goalProfit >= 0 ? '+' : ''}$${goalProfit.toFixed(2)}` : '—'}
                      </td>
                      <td className={pnlClass}>
                        {rowPnL != null ? (
                          <>
                            {rowPnL >= 0 ? '+' : ''}${rowPnL.toFixed(2)}
                            {pnlPct != null && <span className="portfolio-pct"> ({pnlPct.toFixed(2)}%)</span>}
                          </>
                        ) : '—'}
                      </td>
                      <td className="action-btns">
                        {remaining > 0 && (
                          <button
                            className="sell-btn"
                            onClick={() => openSellForm(i, h.currentPrice)}
                            title="Sell"
                          >
                            Sell
                          </button>
                        )}
                        <button className="remove-btn" onClick={() => handleRemove(i)} title="Remove">✕</button>
                      </td>
                    </tr>
                    {sellIndex === i && (
                      <tr className="sell-row">
                        <td colSpan={12}>
                          <div className="sell-form">
                            <span className="sell-form-label">Sell {h.symbol}:</span>
                            <input
                              type="number"
                              placeholder={`Qty (max ${remaining})`}
                              value={sellQty}
                              onChange={e => setSellQty(e.target.value)}
                              min="0.001"
                              max={remaining}
                              step="any"
                            />
                            <input
                              type="number"
                              placeholder="Sell Price ($)"
                              value={sellPrice}
                              onChange={e => setSellPrice(e.target.value)}
                              min="0.01"
                              step="any"
                            />
                            <button
                              className="sell-confirm-btn"
                              onClick={() => handleSell(i)}
                              disabled={selling || !sellQty || !sellPrice}
                            >
                              {selling ? 'Selling…' : 'Confirm'}
                            </button>
                            <button
                              className="sell-cancel-btn"
                              onClick={() => setSellIndex(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
